import type { ProfileConfig } from "@worker/config-schema";
import { el } from "../../lib/dom";
import { widgetShell, fetchWidget, formatCount } from "./base";

type DiscordServerWidget = Extract<
  ProfileConfig["widgets"][number],
  { type: "discord-server" }
>;

type DiscordServerData = {
  serverId: string;
  serverName: string;
  instantInvite: string | null;
  onlineCount: number;
  memberCount: number | null;
  iconUrl: string | null;
  bannerUrl: string | null;
  description: string | null;
  widgetDisabled?: boolean;
};

export function mountDiscordServerWidget(cfg: DiscordServerWidget): HTMLElement {
  const shell = widgetShell({
    kind: "discord-server",
    title: "Discord Server",
    accent: "#5865f2",
  });
  shell.setLoading();

  const controller = new AbortController();
  shell.root.addEventListener("bio:teardown", () => controller.abort());
  const hide = new Set(cfg.hide ?? []);
  shell.root.setAttribute("data-size", cfg.size ?? "md");
  // Keep last-known-good UI visible if a later refresh fails transiently.
  let hasRendered = false;

  const render = async () => {
    const env = await fetchWidget<DiscordServerData>(
      "discord-server",
      { id: cfg.serverId },
      { signal: controller.signal },
    );
    if (!env.ok || !env.data) {
      if (!hasRendered) shell.setError(env.error ?? "unavailable");
      return;
    }
    const d = env.data;
    if (d.widgetDisabled) {
      if (!hasRendered) shell.setError("widget disabled on server");
      return;
    }
    hasRendered = true;

    shell.body.innerHTML = "";

    // Optional banner across the top (shrinks gracefully on narrow widgets).
    const bannerToUse = cfg.bannerUrl ?? d.bannerUrl;
    if (bannerToUse && !hide.has("banner")) {
      const banner = el("div", {
        class: "widget-dserver__banner",
        style: `background-image:url(${JSON.stringify(bannerToUse)})`,
      });
      shell.body.append(banner);
    }

    const body = el("div", { class: "widget-dserver__body" });

    // Icon: explicit config override wins, then API result, then fallback.
    const resolvedIcon = cfg.iconUrl ?? d.iconUrl;
    const icon = el("div", { class: "widget-dserver__icon" });
    if (resolvedIcon) {
      icon.append(el("img", { src: resolvedIcon, alt: "", draggable: false }));
    } else {
      icon.classList.add("widget-dserver__icon--fallback");
      icon.append(
        el("span", {}, [d.serverName.charAt(0).toUpperCase() || "?"]),
      );
    }

    const main = el("div", { class: "widget-dserver__main" });
    main.append(el("div", { class: "widget-dserver__name" }, [d.serverName]));
    if (d.description && !hide.has("description")) {
      main.append(
        el("div", { class: "widget-dserver__desc" }, [d.description]),
      );
    }

    const stats = el("div", { class: "widget-dserver__stats" });
    stats.append(
      el("div", { class: "widget-dserver__stat" }, [
        el("span", { class: "widget-dserver__dot widget-dserver__dot--on" }),
        el("span", { class: "widget-dserver__stat-num" }, [formatCount(d.onlineCount)]),
        el("span", { class: "widget-dserver__stat-lbl" }, ["online"]),
      ]),
    );
    if (d.memberCount != null && !hide.has("memberCount")) {
      stats.append(
        el("div", { class: "widget-dserver__stat" }, [
          el("span", { class: "widget-dserver__dot" }),
          el("span", { class: "widget-dserver__stat-num" }, [formatCount(d.memberCount)]),
          el("span", { class: "widget-dserver__stat-lbl" }, ["members"]),
        ]),
      );
    }
    main.append(stats);

    body.append(icon, main);

    // Join button goes at the RIGHT edge of the widget row (alongside the
    // icon + main content), not under the stats — that way the icon stays
    // vertically centered relative to the main text and doesn't get pushed
    // off-axis by the button's height.
    const invite = cfg.inviteUrl ?? d.instantInvite;
    if (invite && !hide.has("join")) {
      body.append(
        el(
          "a",
          {
            class: "widget-dserver__join",
            href: invite,
            target: "_blank",
            rel: "noopener noreferrer",
          },
          ["Join"],
        ),
      );
    }

    shell.body.append(body);
  };

  void render();
  const interval = window.setInterval(render, 60_000);
  shell.root.addEventListener("bio:teardown", () => window.clearInterval(interval));

  return shell.root;
}
